import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, PlusCircle, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import IntelligenceHub from "./IntelligenceHub";
import DossierTimeline from "@/components/altiora/DossierTimeline";
import { SectionTitle } from "./atoms";
import { useUserLocation } from "@/hooks/useUserLocation";

const inputCls =
  "w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2 text-sm text-[#1F2937]";
const labelCls = "block text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-1.5";

export default function AdvisorStrategy() {
  const { city: userCity } = useUserLocation();
  const citiesHint = userCity ? `${userCity}, autres villes…` : "Vos villes cibles…";
  const [vocab, setVocab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    market_mix: [],
    countries: [], provinces: [], cities: [], languages: [],
    communities: [], professional_sectors: [], opportunity_types: [],
    income_bucket: "", family_situation: "", notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [{ data: v }, { data: s }] = await Promise.all([
          api.get("/cdl/vocabulary"),
          api.get("/cdl/strategy"),
        ]);
        setVocab(v);
        if (s.strategy) {
          setF((prev) => ({ ...prev, ...s.strategy, market_mix: s.strategy.market_mix || [] }));
        }
      } catch {
        toast.error("Chargement stratégie impossible.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setList = (k, csv) => set(k, csv.split(",").map((x) => x.trim()).filter(Boolean));

  const totalWeight = (f.market_mix || []).reduce((a, m) => a + Number(m.weight || 0), 0);

  const addMix = (persona) => {
    if (f.market_mix.some((m) => m.persona === persona)) return;
    set("market_mix", [...f.market_mix, { persona, weight: 10 }]);
  };
  const updateMix = (i, w) => {
    const next = [...f.market_mix];
    next[i] = { ...next[i], weight: Math.max(0, Math.min(100, parseInt(w || "0", 10) || 0)) };
    set("market_mix", next);
  };
  const removeMix = (i) => set("market_mix", f.market_mix.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        income_bucket: f.income_bucket || null,
        family_situation: f.family_situation || null,
      };
      await api.put("/cdl/strategy", payload);
      toast.success("Stratégie enregistrée. Le radar s'ajuste immédiatement.");
    } catch (e) {
      toast.error("Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <IntelligenceHub activeTab="strategy">
        <div className="flex items-center justify-center py-16 text-[#5B5B62]">
          <Loader2 className="h-5 w-5 animate-spin mr-2" strokeWidth={1.8} /> Chargement…
        </div>
      </IntelligenceHub>
    );
  }

  return (
    <IntelligenceHub activeTab="strategy">
      <div className="max-w-3xl space-y-8">
        <div>
          <SectionTitle sub="La répartition doit refléter votre ambition commerciale. Le total suggéré est 100 %, mais l'IA renormalise.">
            Répartition stratégique du marché
          </SectionTitle>

          <div className="rounded-md border border-[#E4E1D8] bg-white divide-y divide-[#F2EFE7]">
            {f.market_mix.length === 0 ? (
              <div className="px-4 py-6 text-xs text-[#8B8B8F] text-center">
                Aucun persona sélectionné. Ajoutez-en un ci-dessous.
              </div>
            ) : (
              f.market_mix.map((m, i) => (
                <div key={m.persona} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#1F2937]">
                      {(vocab?.personas || []).find((p) => p.key === m.persona)?.label_fr || m.persona}
                    </div>
                  </div>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={m.weight}
                    onChange={(e) => updateMix(i, e.target.value)}
                    className={`${inputCls} w-24 text-right`}
                    data-testid={`strat-mix-${m.persona}`}
                  />
                  <span className="text-xs text-[#5B5B62]">%</span>
                  <button onClick={() => removeMix(i)} className="text-[#8B8B8F] hover:text-rose-600">
                    <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>
              ))
            )}
            <div className="px-4 py-2.5 flex items-center justify-between bg-[#F8F7F3]">
              <span className="text-xs text-[#5B5B62]">Total</span>
              <span className={`text-sm font-semibold ${totalWeight === 100 ? "text-emerald-700" : "text-[#8A5A00]"}`}>
                {totalWeight}%
              </span>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {(vocab?.personas || []).filter((p) => !f.market_mix.some((m) => m.persona === p.key)).map((p) => (
              <button
                key={p.key}
                onClick={() => addMix(p.key)}
                className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs border border-[#E4E1D8] bg-white text-[#0B2545] hover:border-[#0B2545] transition-colors"
                data-testid={`strat-add-${p.key}`}
              >
                <PlusCircle className="h-3 w-3" strokeWidth={1.8} />
                {p.label_fr}
              </button>
            ))}
          </div>
        </div>

        <div>
          <SectionTitle sub="Ces filtres orientent la qualification IA et le radar quotidien.">Zone géographique & langue</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Pays (ISO-2, séparés par virgule)">
              <input data-testid="strat-countries" value={(f.countries || []).join(", ")} onChange={(e) => setList("countries", e.target.value)} className={inputCls} placeholder="CA, CH" />
            </Field>
            <Field label="Provinces">
              <input value={(f.provinces || []).join(", ")} onChange={(e) => setList("provinces", e.target.value)} className={inputCls} placeholder="Québec, Ontario" />
            </Field>
            <Field label="Villes">
              <input value={(f.cities || []).join(", ")} onChange={(e) => setList("cities", e.target.value)} className={inputCls} placeholder={citiesHint} />
            </Field>
            <Field label="Langues">
              <input value={(f.languages || []).join(", ")} onChange={(e) => setList("languages", e.target.value)} className={inputCls} placeholder="fr, en" />
            </Field>
            <Field label="Communautés">
              <input value={(f.communities || []).join(", ")} onChange={(e) => setList("communities", e.target.value)} className={inputCls} placeholder="Maghrébine, Latino, Chinoise…" />
            </Field>
            <Field label="Secteurs professionnels">
              <input value={(f.professional_sectors || []).join(", ")} onChange={(e) => setList("professional_sectors", e.target.value)} className={inputCls} placeholder="Santé, TI, Construction…" />
            </Field>
          </div>
        </div>

        <div>
          <SectionTitle sub="Décrivez les opportunités que vous cherchez à générer.">Focus commercial</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Niveau de revenu visé">
              <select value={f.income_bucket || ""} onChange={(e) => set("income_bucket", e.target.value)} className={inputCls}>
                <option value="">Indifférent</option>
                <option value="low">Bas</option>
                <option value="mid">Moyen</option>
                <option value="high">Élevé</option>
                <option value="very_high">Très élevé</option>
              </select>
            </Field>
            <Field label="Situation familiale">
              <select value={f.family_situation || ""} onChange={(e) => set("family_situation", e.target.value)} className={inputCls}>
                <option value="">Indifférent</option>
                <option value="single">Célibataire</option>
                <option value="couple">Couple sans enfant</option>
                <option value="family_children">Famille avec enfants</option>
                <option value="any">Tous types</option>
              </select>
            </Field>
            <Field label="Types d'opportunité recherchés">
              <input value={(f.opportunity_types || []).join(", ")} onChange={(e) => setList("opportunity_types", e.target.value)} className={inputCls} placeholder="Partenariat organisme, référencement notaire, cohorte étudiants…" />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Notes stratégiques (privées)">
              <textarea rows={4} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
            </Field>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            data-testid="strat-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white font-medium px-5 py-2 text-sm transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <Save className="h-4 w-4" strokeWidth={1.8} />}
            Enregistrer ma stratégie
          </button>
        </div>
      </div>
    </IntelligenceHub>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className={labelCls}>{label}</div>{children}</label>;
}
